import { Tag } from 'antd';
import type { ReactNode } from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  HourglassOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  StopOutlined,
  SyncOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

/**
 * Job / Task 状态可视化 Tag。
 *
 * 与后端 ``JobStatusEnum`` / ``TaskStatusEnum`` 对齐，按 ``status`` (id) 选色与图标，
 * 文案沿用后端返回的 ``statusName``（多端文案统一）。
 *
 * 颜色映射哲学：
 * - 进行中 / 排队 → 蓝色 processing 系（带 SyncOutlined spin 表示活动中）
 * - 通过 / 成功    → 绿色 success
 * - 不通过        → 黄色 warning（评测结论"不通过"，业务结论而非异常）
 * - 执行 / 系统异常 → 红色 error
 * - 崩溃 coredump  → 火山色 volcano + 闪电图标（进程 core dump 是区别于普通失败的
 *                    严重崩溃，单独配色 + ThunderboltOutlined，长列表里一眼可辨）
 * - 取消 / 未开始  → 灰色 default
 *
 * 这样一眼能看出「在跑 / 完成 / 出错 / 崩溃 / 待办」几类，长列表扫视成本最低。
 */

interface StatusVisual {
  color: string;
  icon: ReactNode;
}

/** status id → 颜色 + 图标，未声明的 id（含 unknown=30）用 fallback。 */
const STATUS_VISUAL: Record<number, StatusVisual> = {
  1:  { color: 'default',    icon: <StopOutlined /> },                   // 取消
  3:  { color: 'default',    icon: <ClockCircleOutlined /> },            // 未开始
  5:  { color: 'blue',       icon: <HourglassOutlined /> },              // 排队中
  7:  { color: 'processing', icon: <SyncOutlined spin /> },              // 编译中 (job)
  10: { color: 'processing', icon: <SyncOutlined spin /> },              // 执行中
  15: { color: 'success',    icon: <CheckCircleOutlined /> },            // 通过 / succeeded
  20: { color: 'warning',    icon: <ExclamationCircleOutlined /> },      // 不通过
  25: { color: 'error',      icon: <CloseCircleOutlined /> },            // 执行异常
  30: { color: 'red',        icon: <QuestionCircleOutlined /> },         // 系统异常 (unknown)
  35: { color: 'error',      icon: <CloseCircleOutlined /> },            // 编译失败 (job)
  40: { color: 'volcano',    icon: <ThunderboltOutlined /> },            // 崩溃 coredump (task)
};

const FALLBACK_VISUAL: StatusVisual = {
  color: 'default',
  icon: <MinusCircleOutlined />,
};

interface JobStatusTagProps {
  /** 后端 JobStatusEnum / TaskStatusEnum 的 id；缺失时退化为纯文字 */
  status?: number | null;
  /** 后端返回的 ``statusName`` 中文展示文案；缺失时退化为 ``未知(<id>)`` 或 "-" */
  statusName?: string | null;
  /** Tag 内左侧是否显示图标，默认 true */
  showIcon?: boolean;
  /** Tag 最小宽度（px），用于在表格列里把不同长度的状态对齐显示。
   *  传 0/null 可关闭固定宽度，让 Tag 按内容收缩。 */
  minWidth?: number | null;
  /** Tag 内文字右侧的附加内容（如下拉三角），放在 Tag 内避免窄列截断。 */
  suffix?: ReactNode;
}

/** 默认最小宽度 = 4 个中文字 + icon + 内边距，覆盖 ``执行异常`` / ``系统异常`` 这类最长文案。 */
const DEFAULT_MIN_WIDTH = 88;

export default function JobStatusTag({
  status,
  statusName,
  showIcon = true,
  minWidth = DEFAULT_MIN_WIDTH,
  suffix,
}: JobStatusTagProps) {
  if (!statusName && (status === null || status === undefined)) {
    return <span style={{ color: 'rgba(0,0,0,0.45)' }}>-</span>;
  }

  const text = statusName || `未知(${status})`;
  const visual =
    typeof status === 'number' ? STATUS_VISUAL[status] || FALLBACK_VISUAL : FALLBACK_VISUAL;

  return (
    <Tag
      color={visual.color}
      icon={showIcon ? visual.icon : undefined}
      style={{
        marginInlineEnd: 0,
        minWidth: minWidth ?? undefined,
        textAlign: 'center',
      }}
    >
      {text}
      {suffix}
    </Tag>
  );
}
